import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { Keyboard, Pressable, ScrollView, Text, TextInput, View, useWindowDimensions } from 'react-native'
import { useController, type Control, type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { isWeb } from '@/constants/app'
import { COUNTRY_CODES } from '@/constants/config'
import { cn } from '@/utils/cn'
import { Input, type InputProps } from '@/components/base'
import { ArrowBack, Cancel, ExpandMore } from '@/components/icon'
import ScreenHeader from '@/components/ScreenHeader'
import SlideInModal, { useSlideInModal } from '@/components/SlideInModal'

interface PhoneInputProps<
  TValues extends FieldValues,
  TCodePath extends FieldPath<TValues>,
  TNumberPath extends FieldPath<TValues>
> extends Omit<InputProps, 'defaultValue' | 'error' | 'onChange' | 'value'> {
  label?: string
  placeholder?: string
  hint?: string
  control: Control<TValues>
  name: {
    code: TCodePath
    number: TNumberPath
  }
  rules?: {
    code?: RegisterOptions<TValues, TCodePath>
    number?: RegisterOptions<TValues, TNumberPath>
  }
}

export function PhoneInput<
  TValues extends FieldValues,
  TCodePath extends FieldPath<TValues>,
  TNumberPath extends FieldPath<TValues>
>({
  label,
  placeholder,
  hint,
  control,
  name,
  rules,
  className,
  ...props
}: PhoneInputProps<TValues, TCodePath, TNumberPath>) {
  const { t } = useTranslation()
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [isLateFocus, setIsLateFocus] = useState(false)
  const [isClearBtnFocus, setIsClearBtnFocus] = useState(false)
  const inputRef: MutableRefObject<TextInput | null> = useRef(null)
  const code = useController({ control, name: name.code, rules: rules?.code })
  const number = useController({ control, name: name.number, rules: rules?.number })
  const hasError = number.fieldState.error !== undefined
  const hasPlaceholder = !hasError && !hint
  const hasClearButton = isWeb ? (isClearBtnFocus || isLateFocus) && number.field.value : isFocus && number.field.value
  const { fontScale } = useWindowDimensions()
  const baseSelectorWidth = COUNTRY_CODE_WIDTH[code.field.value.length] ?? COUNTRY_CODE_WIDTH[3]
  const selectorWidth = baseSelectorWidth * Math.max(1, fontScale)

  useEffect(() => {
    setIsLateFocus(isFocus)
  }, [isFocus])

  return (
    <>
      <View className={'gap-y-1'}>
        {label && <Text className={'text-sm font-medium text-text-secondary'}>{label}</Text>}
        <Input
          {...props}
          testID={props.testID ?? `input-${String(name.number).replaceAll('.', '-')}`}
          accessibilityLabel={props.accessibilityLabel ?? label ?? placeholder}
          ref={(e) => {
            number.field.ref(e)
            inputRef.current = e
          }}
          value={number.field.value}
          onChange={number.field.onChange}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          leftElement={
            <SelectCountryCode
              value={code.field.value}
              onChange={code.field.onChange}
              hasError={hasError}
              width={selectorWidth}
            />
          }
          rightElement={
            hasClearButton ? (
              <Pressable
                accessibilityLabel={t('auth:clearPhoneNumber')}
                accessibilityRole={'button'}
                className={cn('size-8 items-center justify-center outline-offset-1', hasError && 'outline-danger')}
                hitSlop={6}
                onPress={() => {
                  number.field.onChange('')
                  setIsClearBtnFocus(false)
                  inputRef.current?.focus()
                }}
                onFocus={() => setIsClearBtnFocus(true)}
                onBlur={() => setIsClearBtnFocus(false)}
              >
                <Cancel className={'size-5 text-text-quaternary'} />
              </Pressable>
            ) : undefined
          }
          placeholder={fontScale > 1.5 ? undefined : placeholder}
          error={hasError}
          keyboardType={'phone-pad'}
          maxLength={15}
          className={className}
        />
        {hint && <Text className={'text-xs font-medium text-text-tertiary'}>{hint}</Text>}
        {hasError && <Text className={'text-xs font-medium text-text-danger'}>{number.fieldState.error?.message}</Text>}
        {hasPlaceholder && <View className={'h-4'} />}
      </View>
    </>
  )
}

const COUNTRY_CODE_WIDTH = [60, 72, 84, 96]
const DURATION = 300

interface SelectCountryCodeProps {
  value: string
  onChange: (value: string) => void
  hasError: boolean
  width: number
}

function SelectCountryCode({ value, onChange, hasError, width }: SelectCountryCodeProps) {
  const { visible, open, close, offset } = useSlideInModal(DURATION)
  const { t } = useTranslation()

  const openSelector = () => {
    Keyboard.dismiss()
    open()
  }

  const select = (value: string) => {
    onChange(value)
    close()
  }

  return (
    <>
      <Pressable
        accessibilityLabel={t('auth:countryOrRegion')}
        accessibilityRole={'button'}
        accessibilityValue={{ text: `+${value}` }}
        className={cn('h-full flex-row items-center', hasError && 'outline-danger')}
        onPress={openSelector}
        style={{ width }}
      >
        <View className={'h-full flex-1 flex-row items-center border-r border-border-20 px-3'}>
          <Text className={'flex-1'}>{`+${value}`}</Text>
          <ExpandMore className={'size-5 text-text-quaternary'} />
        </View>
      </Pressable>
      <SlideInModal visible={visible} offset={offset} close={close}>
        <ScreenHeader
          leftElement={
            <Pressable className={'absolute left-4 size-6 justify-center'} onPress={close}>
              <ArrowBack className={'size-6 text-text-tertiary'} />
            </Pressable>
          }
          centerElement={<Text className={'text-base font-medium text-text-primary'}>{t('auth:countryOrRegion')}</Text>}
          hasBottomBorder={true}
        />
        <ScrollView className={'h-full flex-1 bg-background-secondary pb-[44px]'} keyboardShouldPersistTaps={'handled'}>
          {Object.entries(COUNTRY_CODES).map(([key, value]) => (
            <Pressable
              key={key}
              accessibilityLabel={`${value.flag} ${t(`auth:country.${key}`)}, +${value.dial_code}`}
              accessibilityRole={'button'}
              onPress={() => select(value.dial_code)}
            >
              {({ pressed }) => {
                return (
                  <View className={cn('flex-row justify-between bg-white px-4 py-3', pressed && 'bg-fill-flat-active')}>
                    <View className={'flex-row items-center gap-x-3'}>
                      <Text>{`${value.flag} ${t(`auth:country.${key}`)}`}</Text>
                    </View>
                    <Text className={'text-text-secondary'}>{`+${value.dial_code}`}</Text>
                  </View>
                )
              }}
            </Pressable>
          ))}
        </ScrollView>
      </SlideInModal>
    </>
  )
}
